import type { ReactNode } from 'react';
import {
  Search,
  Target,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import type { AnalyticsDashboard } from '@services/knowledge-analytics.service';
import { CARD, CARD_CLASS } from './shell';

// The Overview section: the old Knowledge Analytics figures, on the surfaces
// the rest of this page uses.
//
// The layout is the copy it was — four metric cards over three stat panels,
// same figures in the same order. What changed is the palette: the originals
// were `Card` from the kit, which paints white, and white cards on the ivory
// canvas read as a panel that belongs to a different application. Everything
// here is drawn from the theme tokens, so it sits with the gap queue and the
// coverage boxes instead of floating above them.
//
// SCALES ARE NOT UNIFORM in this payload and no field says which is which:
//
//   0–1   deflection.rate, search.clickThroughRate, search.zeroResultRate
//   0–100 content.avgHelpfulnessScore, embeddings.healthScore
//
// `ratio()` and `percent()` state that difference once, here, instead of it
// being re-derived at each call site. Getting it wrong renders 0.75 as "1%".

const MONO = 'font-mono';

/** For fields on a 0–1 scale. */
const ratio = (value: number | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value * 100)}%` : '0%';

/** For fields already expressed as whole percent. */
const percent = (value: number | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value)}%` : '0%';

const currency = (value: number | null | undefined): string =>
  `$${(typeof value === 'number' && Number.isFinite(value) ? value : 0).toLocaleString()}`;

const trendPercent = (value: number | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value) ? Math.abs(value).toFixed(1) : '0.0';

export type Accent = 'machine' | 'green' | 'brass' | 'terracotta' | 'burgundy';

/** The four hues the original used, mapped onto the palette this page speaks. */
const ACCENT: Record<Accent, { wash: string; line: string; ink: string }> = {
  machine: {
    wash: 'var(--co-machine-wash)',
    line: 'var(--co-machine-line)',
    ink: 'var(--co-machine-ink)',
  },
  green: {
    wash: 'var(--co-green-wash)',
    line: 'var(--co-green-line)',
    ink: 'var(--co-green-ink)',
  },
  brass: {
    wash: 'var(--co-brass-wash)',
    line: 'var(--co-brass-line)',
    ink: 'var(--co-brass-ink)',
  },
  terracotta: {
    wash: 'var(--co-terracotta-wash)',
    line: 'var(--co-terracotta-line)',
    ink: 'var(--co-terracotta)',
  },
  burgundy: {
    wash: 'var(--co-burgundy-wash)',
    line: 'var(--co-burgundy-line)',
    ink: 'var(--co-burgundy)',
  },
};

/**
 * The metric card, exported so the gap queue's four figures are literally the
 * same component as the overview's. They were drifting: the gaps tab had grown
 * its own ink-coloured strip, which is how one screen ends up with two
 * vocabularies for "here is a number".
 */
export function MetricCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  accent,
  note,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: ReactNode;
  trend?: { direction: 'up' | 'down' | 'stable'; value: number };
  accent: Accent;
  /** Hover-only, so it never changes the layout. */
  note?: string;
}) {
  const tone = ACCENT[accent];
  return (
    <div className={`${CARD_CLASS} min-w-0 px-[18px] py-4`} style={CARD}>
      <div className="flex items-start justify-between gap-3">
        <div
          className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg border"
          style={{ background: tone.wash, borderColor: tone.line, color: tone.ink }}
        >
          {icon}
        </div>
        {trend && trend.direction !== 'stable' && (
          <div
            className={`${MONO} flex flex-none items-center gap-1 text-[11.5px] font-semibold tabular-nums`}
            style={{
              color:
                trend.direction === 'up' ? 'var(--co-green-ink)' : 'var(--co-burgundy)',
            }}
          >
            {trend.direction === 'up' ? (
              <TrendingUp className="h-[13px] w-[13px]" />
            ) : (
              <TrendingDown className="h-[13px] w-[13px]" />
            )}
            {`${trendPercent(trend.value)}%`}
          </div>
        )}
      </div>
      <div className="mt-[13px]">
        <p
          className={`${MONO} text-[26px] font-semibold leading-none tabular-nums text-[var(--co-ink)]`}
          title={note}
        >
          {value}
        </p>
        <p
          className={`${MONO} mt-[9px] text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]`}
        >
          {title}
        </p>
        {subtitle && (
          <p className="mt-[5px] text-[11.5px] leading-[1.4] text-[var(--co-ink-4)]">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={`${CARD_CLASS} min-w-0 px-5 py-4`} style={CARD}>
      <h4 className="text-[15px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
        {title}
      </h4>
      <dl className="mt-[14px] flex flex-col gap-[11px]">{children}</dl>
    </div>
  );
}

function StatRow({
  label,
  value,
  tone,
  note,
}: {
  label: string;
  value: ReactNode;
  tone?: string;
  note?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="min-w-0 truncate text-[12.5px] text-[var(--co-ink-3)]" title={note}>
        {label}
        {note && (
          <span className="ml-[5px] text-[var(--co-ink-4)]" aria-hidden="true">
            †
          </span>
        )}
      </dt>
      <dd
        className={`${MONO} flex-none text-[14px] font-semibold tabular-nums`}
        style={{ color: tone ?? 'var(--co-ink)' }}
      >
        {value}
      </dd>
    </div>
  );
}

export function InsightsStats({ dashboard }: { dashboard: AnalyticsDashboard | null }) {
  // No payload means no figures. Seven boxes reading zero would be
  // indistinguishable from a quiet month.
  if (!dashboard) {
    return (
      <div className={`${CARD_CLASS} px-6 py-8 text-center`} style={CARD}>
        <p className="text-[12.5px] text-[var(--co-ink-3)]">
          Analytics could not be loaded for this period.
        </p>
      </div>
    );
  }

  const { search, content, deflection, gaps, embeddings } = dashboard;
  const waiting = (embeddings?.pendingCount ?? 0) + (embeddings?.staleCount ?? 0);
  const zeroResult = search?.zeroResultRate ?? 0;

  return (
    <>
      <div className="grid grid-cols-2 gap-[14px] md:grid-cols-4">
        <MetricCard
          title="Total Searches"
          value={(search?.totalSearches ?? 0).toLocaleString()}
          icon={<Search className="h-[17px] w-[17px]" />}
          trend={{
            direction: search?.trendDirection ?? 'stable',
            value: search?.trendPercentage ?? 0,
          }}
          accent="machine"
        />
        <MetricCard
          title="Deflection Rate"
          value={ratio(deflection?.rate)}
          subtitle={`${deflection?.totalDeflected ?? 0} deflected`}
          icon={<Target className="h-[17px] w-[17px]" />}
          accent="green"
        />
        <MetricCard
          title="Estimated Savings"
          value={currency(deflection?.estimatedSavings)}
          subtitle="This period"
          icon={<DollarSign className="h-[17px] w-[17px]" />}
          accent="brass"
          note="The API returns this as a bare number with no currency field; the symbol is the UI's assumption."
        />
        <MetricCard
          title="Knowledge Gaps"
          value={gaps?.totalOpen ?? 0}
          subtitle={`${gaps?.highPriority ?? 0} high priority`}
          icon={<AlertTriangle className="h-[17px] w-[17px]" />}
          accent={(gaps?.highPriority ?? 0) > 0 ? 'burgundy' : 'terracotta'}
        />
      </div>

      <div className="grid grid-cols-1 gap-[14px] md:grid-cols-3">
        <Panel title="Content Overview">
          <StatRow label="Total Articles" value={content?.totalArticles ?? 0} />
          <StatRow label="Total Views" value={(content?.totalViews ?? 0).toLocaleString()} />
          <StatRow
            label="Avg Helpfulness"
            value={percent(content?.avgHelpfulnessScore)}
            note="The payload supplies the share but not how many ratings it is drawn from. Article Performance shows the counts."
          />
        </Panel>

        <Panel title="Search Quality">
          <StatRow label="Unique Queries" value={search?.uniqueQueries ?? 0} />
          <StatRow
            label="Click-through Rate"
            value={ratio(search?.clickThroughRate)}
            tone={(search?.clickThroughRate ?? 0) === 0 ? 'var(--co-burgundy)' : undefined}
            note={
              (search?.clickThroughRate ?? 0) === 0
                ? 'Nobody opened a result. Either the answers are not useful, or clicks are not being recorded.'
                : undefined
            }
          />
          <StatRow
            label="Zero Result Rate"
            value={ratio(zeroResult)}
            tone={zeroResult > 0.2 ? 'var(--co-burgundy)' : undefined}
          />
        </Panel>

        <Panel title="Embedding Health">
          <StatRow
            label="Health Score"
            value={
              <span
                className={`${MONO} inline-flex items-center rounded-[5px] border px-2 py-[3px] text-[10.5px] font-semibold`}
                style={
                  (embeddings?.healthScore ?? 0) > 90
                    ? {
                        borderColor: 'var(--co-green-line)',
                        background: 'var(--co-green-wash)',
                        color: 'var(--co-green-ink)',
                      }
                    : (embeddings?.healthScore ?? 0) > 70
                      ? {
                          borderColor: 'var(--co-terracotta-line)',
                          background: 'var(--co-terracotta-wash)',
                          color: 'var(--co-terracotta)',
                        }
                      : {
                          borderColor: 'var(--co-burgundy-line)',
                          background: 'var(--co-burgundy-wash)',
                          color: 'var(--co-burgundy)',
                        }
                }
              >
                {percent(embeddings?.healthScore)}
              </span>
            }
          />
          <StatRow
            label="Embedded"
            value={`${embeddings?.embeddedCount ?? 0}/${embeddings?.totalSources ?? 0}`}
          />
          {/* This can exceed the gap between Embedded and its total, and that
              is not a contradiction: `staleCount` counts sources that have
              FINISHED embedding and have since been edited, so a stale source
              is also a counted embedded one (knowledge-service #9103). The
              Embeddings section splits the two apart. */}
          <StatRow
            label="Pending/Stale"
            value={waiting}
            tone={waiting > 0 ? 'var(--co-terracotta-ink)' : undefined}
            note="Stale means finished-then-edited, so these overlap with Embedded rather than contradicting it."
          />
        </Panel>
      </div>
    </>
  );
}
